// RegisterPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaUser, FaEnvelope, FaLock, FaEyeSlash, FaEye } from 'react-icons/fa';
import { GraduationCap } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import AppContext from '../../../../context/AppContext';
import '../../../../assets/css/student/dashboard/Auth.css';

function RegisterPage() {
    const navigate = useNavigate();
    const { baseUrl, regExpChecker } = useContext(AppContext);
    const [searchParams] = useSearchParams();

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [fullNameError, setFullNameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    useEffect(() => {
        const studentEmail = localStorage.getItem('studentEmail');
        const studentPassword = localStorage.getItem('studentPassword');
        if (studentEmail && studentPassword) {
            setIsLoading(true);
            navigate('/student/dashboard');
        }
        const emailParam = searchParams.get('email');
        if (emailParam) setEmail(emailParam);
    }, []);

    const studentRegister = (e) => {
        e.preventDefault();
        setIsLoading(true);

        const formdata = new FormData();
        formdata.append('fullName', fullName);
        formdata.append('email', email);
        formdata.append('password', password);

        axios
            .post(`${baseUrl}/student/auth/studentsignup`, formdata)
            .then((response) => {
                if (response.data.error) {
                    setErrorMessage(response.data.error);
                    setIsLoading(false);
                } else if (response.data.success === true) {
                    localStorage.setItem('studentEmail', response.data.student.email);
                    localStorage.setItem('studentPassword', response.data.student.password);
                    navigate('/student/dashboard');
                } else {
                    setErrorMessage(response.data.message || 'Something went wrong!');
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                setErrorMessage(error.response?.data?.message || 'Something went wrong!');
                setIsLoading(false);
            });
    };

    return isLoading ? (
        <div className="sta-loading-screen">
            <Spinner style={{ height: 50, width: 50 }} />
        </div>
    ) : (
        <div className="sta-auth-page">

            {/* ── LEFT PANEL ── */}
            <div className="sta-left-panel">
                <div className="sta-left-blob sta-left-blob--top" />
                <div className="sta-left-blob sta-left-blob--bottom" />
                <div className="sta-left-content">
                    <div className="sta-left-logo">
                        <GraduationCap size={34} />
                    </div>
                    <h1 className="sta-left-brand">Student Support Hub</h1>
                    <p className="sta-left-tagline">
                        Join thousands of students. Create your account and get started today.
                    </p>
                    <div className="sta-left-deco">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="sta-right-panel">
                <div className="sta-form-box sta-fade-in">

                    <div className="sta-form-header">
                        <h2 className="sta-form-title">Create account</h2>
                        <p className="sta-form-sub">Fill in your details to get started</p>
                    </div>

                    <form className="sta-form" onSubmit={studentRegister}>

                        {/* Full Name */}
                        <div className="sta-field">
                            <label className="sta-label">Full Name</label>
                            <div className="sta-input-wrap">
                                <FaUser className="sta-icon" />
                                <input
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={fullName}
                                    onChange={(e) => {
                                        setFullName(e.target.value);
                                        setFullNameError(regExpChecker('username', e.target.value));
                                    }}
                                    className={`sta-input ${fullNameError ? 'sta-input--error' : ''}`}
                                    autoComplete="off"
                                />
                            </div>
                            {fullNameError && <p className="sta-field-error">{fullNameError}</p>}
                        </div>

                        {/* Email */}
                        <div className="sta-field">
                            <label className="sta-label">Email Address</label>
                            <div className="sta-input-wrap">
                                <FaEnvelope className="sta-icon" />
                                <input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setEmailError(regExpChecker('email', e.target.value));
                                    }}
                                    className={`sta-input ${emailError ? 'sta-input--error' : ''}`}
                                    autoComplete="off"
                                />
                            </div>
                            {emailError && <p className="sta-field-error">{emailError}</p>}
                        </div>

                        {/* Password */}
                        <div className="sta-field">
                            <label className="sta-label">Password</label>
                            <div className="sta-input-wrap">
                                <FaLock className="sta-icon" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError(regExpChecker('password', e.target.value));
                                    }}
                                    className={`sta-input ${passwordError ? 'sta-input--error' : ''}`}
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className="sta-eye-btn"
                                    onClick={() =>
                                        setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {passwordError && <p className="sta-field-error">{passwordError}</p>}
                        </div>

                        {/* Confirm Password */}
                        <div className="sta-field">
                            <label className="sta-label">Confirm Password</label>
                            <div className="sta-input-wrap">
                                <FaLock className="sta-icon" />
                                <input
                                    type="password"
                                    placeholder="Re-enter your password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setConfirmPasswordError(regExpChecker('confirmPassword', e.target.value, password));
                                    }}
                                    className={`sta-input ${confirmPasswordError ? 'sta-input--error' : ''}`}
                                />
                            </div>
                            {confirmPasswordError && <p className="sta-field-error">{confirmPasswordError}</p>}
                        </div>

                        {errorMessage && (
                            <div className="sta-global-error">{errorMessage}</div>
                        )}

                        <button type="submit" className="sta-submit-btn">
                            Register
                        </button>
                    </form>

                    <div className="sta-switch-row">
                        <span>Already have an account?</span>
                        <a
                            className="sta-switch-link"
                            onClick={() => navigate('/student/login')}
                            style={{ cursor: 'pointer' }}
                        >
                            Login here
                        </a>
                    </div>

                </div>
            </div>

        </div>
    );
}

export default RegisterPage;